import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import Table from "@/components/common/Table";
import Button from "@/components/common/Button";
import {ReactComponent as RiEditLine} from "@/assets/icons/ri-edit-line.svg";
import {currencyFormat} from "@/utils";
import Invoice from "@/pages/Invoice/index";
import {colors} from "@/pages/Invoice/constant";
import useCustomPaging from "@/hooks/useCustomPaging";
import {RootState} from "@/store";
import {constructTableMetaFromPaging2} from "@/utils/paging";

const convertToInvoiceRow = (invoices: Invoice[]) : TempInvoiceRow[] => invoices.map(i => ({
  customId: i.custom_id,
  invoiceId: i.id,
  bookingId: i?.booking?.custom_id ?? i.booking_id,
  createdDate: i.created_at,
  status: i.status,
  nominal: i.total_price,
  customerName: i.customer?.name ?? ''
}))

const renderInvoiceId = ({ customId, id }: {customId: string, id: string}, navigate) => (
	<div className="flex flex-col pr-7 gap-3">
		<div className="text-jsm text-black-primary font-bold"
				 onClick={() => navigate(`/dashboard/invoices/${id}`)}
				 role="button"
				 tabIndex={0}
				 onKeyDown={(e) => {e.preventDefault()}}>
        {customId}
		</div>
	</div>
);
const renderAction = (actionId, navigate) => (
  <Button
    onClick={() => {
      navigate(`/dashboard/invoices/${actionId}`)
    }}
    type={null}
    variant="secondary"
    className="border-stroke-subtle border px-4"
  >
    <div className="flex gap-2 justify-center items-center h-4">
      <RiEditLine/>
      Edit
    </div>
  </Button>
)
const renderStatus = (status) => (
  <div
    className={`flex px-4 py-1 w-fit rounded capitalize font-medium
    ${colors[status]}`}>
    {status.replace('_',  ' ')}
  </div>
)
const renderCreatedDate = date => {
  const newDate = new Date(date)
  return (
    <div>
      {newDate.toLocaleDateString()}, {newDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
    </div>
  )
}
const renderNominal = nominal => (
  <div className="text-jsm text-black-primary font-medium">
    {currencyFormat(nominal)}
  </div>
)

const HEADERS: TableHeader[] = [
  {
    label: 'INVOICE ID',
    key: 'customIdAction',
  },
  {
    label: 'Booking ID',
    key: 'bookingId',
  },
  {
    label: 'Customer',
    key: 'customerName',
  },
  {
    label: 'Nominal',
    key: 'nominal',
  },
  {
    label: 'Status',
    key: 'status',
  },
  {
    label: 'Created Date',
    key: 'createdDate',
  },
  // {
  //   label: 'Action',
  //   key: 'actionId'
  // },
];
const constructTableBody = (invoices, navigate) :TableBody => ({
  data: convertToInvoiceRow(invoices).map((s) => ({
    id: s.invoiceId,
    value: {
      ...s,
      actionId: s.invoiceId,
      customIdAction: {
        customId: s.customId,
        id: s.invoiceId,
      },
    } as TempInvoiceRowVO
  })),
  component: {
    customIdAction: (v: {customId: string, id: string})  => renderInvoiceId(v, navigate),
    actionId: (v) => renderAction(v, navigate),
    status: (v) => renderStatus(v),
    nominal: (v) => renderNominal(v),
    createdDate: (v) => renderCreatedDate(v)
  },
})
const SORT: TableSort = {
  type: 'SERVER',
  handler: () => {}
}

interface InvoiceTableProps {
  invoices: Invoice[]
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({ invoices }) => {
  const navigate = useNavigate()
  const { paging } = useSelector((rootState: RootState) => rootState.invoice.state)
  const [,,handlePageNumberChange] = useCustomPaging()

  return (
    <Table
      headers={HEADERS}
      body={constructTableBody(invoices, navigate)}
      sort={SORT}
      meta={constructTableMetaFromPaging2(paging)}
      pageNumberHandler={handlePageNumberChange}
    />
  )
}

export default InvoiceTable;
